import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import { apiService } from '../services/api';
import { Spinner } from 'react-bootstrap';

const EntitySelector = ({ 
  entityType, 
  label, 
  value, 
  onChange, 
  required = false,
  filterBy = null, // e.g., { companyId: 123 } to filter projects by company
  disabled = false
}) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!entityType) return;

    const fetchEntities = async () => {
      try {
        setLoading(true);
        setError(null);

        let response;
        switch (entityType) {
          case 'company':
            response = await apiService.getCompanies(1, 1000); // Get all companies
            if (response.success && response.data) {
              setOptions(response.data.map(c => ({
                id: c.id,
                name: `${c.company || ''}${c.branch ? ` - ${c.branch}` : ''}`.trim() || `Company ${c.id}`,
                value: c.id
              })));
            }
            break;

          case 'project':
            if (filterBy?.companyId) {
              response = await apiService.getProjectsByCompany(filterBy.companyId);
              if (response.success && response.data) {
                setOptions(response.data.map(p => ({
                  id: p.id,
                  name: p.projectName || `Project ${p.id}`,
                  value: p.id
                })));
              } else if (Array.isArray(response)) {
                setOptions(response.map(p => ({
                  id: p.id,
                  name: p.projectName || `Project ${p.id}`,
                  value: p.id
                })));
              }
            } else {
              // If no company filter, show empty or fetch all projects
              setOptions([]);
            }
            break;

          case 'release':
            if (filterBy?.projectId) {
              response = await apiService.getProjectReleases(filterBy.projectId);
              if (response.success && response.data) {
                setOptions(response.data.map(r => ({
                  id: r.id,
                  name: r.release || `Release ${r.id}`,
                  value: r.id
                })));
              } else if (Array.isArray(response)) {
                setOptions(response.map(r => ({
                  id: r.id,
                  name: r.release || `Release ${r.id}`,
                  value: r.id
                })));
              }
            } else {
              setOptions([]);
            }
            break;

          case 'load':
            if (filterBy?.projectId) {
              response = await apiService.getProjectLoads(filterBy.projectId);
              if (response.success && response.data) {
                setOptions(response.data.map(l => ({
                  id: l.id,
                  name: l.loadNo || `Load ${l.id}`,
                  value: l.id
                })));
              } else if (Array.isArray(response)) {
                setOptions(response.map(l => ({
                  id: l.id,
                  name: l.loadNo || `Load ${l.id}`,
                  value: l.id
                })));
              }
            } else {
              setOptions([]);
            }
            break;

          case 'quote':
            if (filterBy?.projectId) {
              response = await apiService.getProjectRetailQuotes(filterBy.projectId);
              if (response.success && response.data) {
                setOptions(response.data.map(q => ({
                  id: q.id,
                  name: `Quote #${q.id}${q.revision ? ` (Rev ${q.revision})` : ''}`,
                  value: q.id
                })));
              } else if (Array.isArray(response)) {
                setOptions(response.map(q => ({
                  id: q.id,
                  name: `Quote #${q.id}${q.revision ? ` (Rev ${q.revision})` : ''}`,
                  value: q.id
                })));
              }
            } else {
              setOptions([]);
            }
            break;

          default:
            setOptions([]);
        }
      } catch (err) {
        console.error(`Error fetching ${entityType}:`, err);
        setError(err.message);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    };

    fetchEntities();
  }, [entityType, filterBy]);

  return (
    <Form.Group className="mb-3">
      <Form.Label>
        {label || entityType.charAt(0).toUpperCase() + entityType.slice(1)}
        {required && <span className="text-danger"> *</span>}
      </Form.Label>
      {loading ? (
        <div className="d-flex align-items-center gap-2">
          <Spinner size="sm" />
          <span className="text-muted">Loading...</span>
        </div>
      ) : (
        <Form.Select
          value={value || ''}
          onChange={(e) => onChange(e.target.value ? parseInt(e.target.value) : null)}
          disabled={disabled || loading}
          required={required}
        >
          <option value="">Select {label || entityType}</option>
          {options.map(option => (
            <option key={option.id} value={option.id}>
              {option.name}
            </option>
          ))}
        </Form.Select>
      )}
      {error && (
        <Form.Text className="text-danger">
          {error}
        </Form.Text>
      )}
      {!loading && options.length === 0 && entityType !== 'company' && !filterBy && (
        <Form.Text className="text-muted">
          Please select a {entityType === 'project' ? 'company' : 'project'} first
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default EntitySelector;

